// src/components/App/GoogleAuthSuccessHandler.jsx

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import useAuth from '../../hooks/useAuth';
import SpinningCustomLogo from '../Common/SpinningCustomLogo';
import posthog from 'posthog-js';

/**
 * This component handles the final step of the OAuth flow.
 * Since the backend only redirects here AFTER successfully linking the account,
 * we just need to refresh the user profile and return to settings.
 */
export default function GoogleAuthSuccessHandler() {
    const { refreshUser, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const isLinked = searchParams.get('linked') === 'true';

        if (isLinked) {
            // Log the success for analytics
            posthog.capture('google_calendar_connected', {
                user_id: user?.uuid,
                email: user?.email
            });

            // Refresh user data to pull the new 'google_id' field
            refreshUser().then(() => {
                navigate('/settings', {
                    replace: true,
                    state: {
                        defaultSection: 'integrations',
                        googleAuthSuccess: true
                    }
                });
            });
        } else {
            // Fallback: If for some reason we land here without linked=true, just go back.
            console.warn("GoogleAuthSuccessHandler reached without linked=true flag.");
            navigate('/settings', { replace: true });
        }
    }, [navigate, refreshUser, searchParams, user]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    maxWidth: '450px'
                }}
            >
                <SpinningCustomLogo size={56} />
                <Typography variant="h5" sx={{ mt: 3, fontWeight: 500 }}>
                    Success!
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Your Google Calendar has been linked. Redirecting you back to your settings...
                </Typography>
            </Paper>
        </Box>
    );
}